import {
  useCallback,
  useRef,
  useSyncExternalStore,
  type ReactEventHandler,
  type ReactNode,
} from 'react'
import { cn } from 'cn'
import type { PostAttachmentType } from 'modules/community/types/post-attachment'

let activeAudioPlayer: HTMLAudioElement | null = null
const activeAudioPlayerListeners = new Set<() => void>()

const subscribe = (listener: () => void): (() => void) => {
  activeAudioPlayerListeners.add(listener)

  return () => {
    activeAudioPlayerListeners.delete(listener)
  }
}

const getServerSnapshot = (): false => false

const setActivePlayer = (player: HTMLAudioElement | null): void => {
  if (activeAudioPlayer === player) return

  const previousPlayer = activeAudioPlayer
  activeAudioPlayer = player
  previousPlayer?.pause()

  for (const listener of activeAudioPlayerListeners) {
    listener()
  }
}

interface AudioPlayerAttachmentProps {
  attachment: PostAttachmentType
  className?: string
}

export const AudioPlayerAttachment = ({
  attachment,
  className,
}: AudioPlayerAttachmentProps): ReactNode => {
  const elementRef = useRef<HTMLAudioElement | null>(null)
  const getSnapshot = useCallback(
    () => activeAudioPlayer !== null && activeAudioPlayer === elementRef.current,
    [],
  )
  const isPlaying = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
  const audioRef = useCallback((element: HTMLAudioElement | null): void => {
    if (element === null && activeAudioPlayer === elementRef.current) setActivePlayer(null)
    elementRef.current = element
  }, [])
  const onPlay: ReactEventHandler<HTMLAudioElement> = event => {
    setActivePlayer(event.currentTarget)
  }
  const onPause: ReactEventHandler<HTMLAudioElement> = event => {
    if (activeAudioPlayer === event.currentTarget) setActivePlayer(null)
  }

  return (
    <audio
      ref={audioRef}
      data-testid="attachment-audio-player"
      data-community-audio-player
      data-playing={isPlaying}
      className={cn('h-[50px] w-full', className)}
      src={attachment.url}
      controls
      preload="auto"
      onPlay={onPlay}
      onPause={onPause}
    />
  )
}
